import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { DataTablePagination } from "@/components/ui/table-pagination";
import { displayErrorMessage } from "@/utils";

import { AggregatorsDataTable } from "./components/AggregatorsDataTable";
import { createColumns } from "./components/columns";
import { DEFAULT_PAGE_SIZE } from "./components/constants";
import { type ListProps } from "./components/types";
import { filterAggregators, getAggregatorRowId } from "./components/utils";

export { AGGREGATOR_SORT_OPTIONS } from "./components/constants";
export type { AggregatorListItem } from "./components/types";

export const List = ({
  aggregators = [],
  errorMessage = "",
  isFetching = false,
  onClickView,
  onSortingChange,
  pageSize = DEFAULT_PAGE_SIZE,
  searchTerm = "",
  sorting,
}: ListProps) => {
  const { t } = useTranslation();
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
  });
  const resolvedSorting = sorting ?? internalSorting;
  const handleSortingChange = onSortingChange ?? setInternalSorting;
  const columns = useMemo(() => createColumns(t, onClickView), [onClickView, t]);
  const filteredAggregators = useMemo(
    () => filterAggregators(aggregators, searchTerm),
    [aggregators, searchTerm]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [resolvedSorting, searchTerm]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(filteredAggregators.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [filteredAggregators.length]);

  const table = useReactTable({
    columns,
    data: filteredAggregators,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getAggregatorRowId,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: handleSortingChange,
    state: {
      pagination,
      sorting: resolvedSorting,
    },
  });

  if (errorMessage) {
    return <>{displayErrorMessage(errorMessage)}</>;
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <AggregatorsDataTable
        emptyMessage={t("aggregators.list.empty")}
        isLoading={isFetching}
        table={table}
      />
      <DataTablePagination table={table} />
    </div>
  );
};

export default List;
